import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Zap, User, Mail, Phone, FileText, Download, Calendar, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuickSignInDashboard = () => {
  const { agent } = useAuth();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [qrData, setQrData] = useState(null);
  const [stats, setStats] = useState({ total: 0, today: 0, thisWeek: 0 });

  useEffect(() => {
    if (agent) {
      fetchQuickSignInLeads();
      fetchQRCode();
    }
  }, [agent]);

  const fetchQuickSignInLeads = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch Quick Sign-In submissions from dedicated endpoint
      const response = await axios.get(`${API}/quick-signin/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const signins = response.data.signins || [];
      
      setLeads(signins);
      calculateStats(signins);
    } catch (error) {
      console.error('Failed to fetch Quick Sign-In submissions:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (leadsData) => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    const today = leadsData.filter(lead => new Date(lead.created_at) >= todayStart).length;
    const thisWeek = leadsData.filter(lead => new Date(lead.created_at) >= weekStart).length;

    setStats({
      total: leadsData.length,
      today,
      thisWeek
    });
  };

  const fetchQRCode = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/quick-signin/qr/${agent.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQrData(response.data);
    } catch (error) {
      console.error('Failed to fetch QR code:', error);
    }
  };

  const downloadQRCode = () => {
    if (!qrData) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${qrData.qr_code}`;
    link.download = `quick-signin-qr-${agent?.name?.replace(/\s+/g, '-')}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR Code downloaded!');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
    });
  };

  const formatPhone = (phone) => {
    if (!phone) return 'N/A';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const downloadExcel = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/quick-signin/export-excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob' // Important for file download
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Quick_Sign_Ins.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Failed to download Excel:', error);
      toast.error('Failed to download Excel file');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A4D2E]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1A4D2E] flex items-center gap-2">
              <Zap className="text-[#1A4D2E]" size={32} />
              Quick Sign-In
            </h1>
            <p className="text-stone-600 mt-1">Manage contact information captured through Quick Sign-In</p>
          </div>
          {leads.length > 0 && (
            <Button
              onClick={downloadExcel}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Download size={18} className="mr-2" />
              Export to Excel
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">Total Sign-Ins</p>
                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-purple-900 mt-1">{stats.total}</p>
              </div>
              <div className="p-3 bg-purple-200 rounded-lg">
                <User className="text-purple-700" size={24} />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Today</p>
                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-900 mt-1">{stats.today}</p>
              </div>
              <div className="p-3 bg-blue-200 rounded-lg">
                <Calendar className="text-blue-700" size={24} />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">This Week</p>
                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-green-900 mt-1">{stats.thisWeek}</p>
              </div>
              <div className="p-3 bg-green-200 rounded-lg">
                <Zap className="text-green-700" size={24} />
              </div>
            </div>
          </Card>
        </div>

        {/* QR Code Section */}
        {qrData && (
          <Card className="p-6 bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-shrink-0">
                <img
                  src={`data:image/png;base64,${qrData.qr_code}`}
                  alt="Quick Sign-In QR Code"
                  className="w-32 h-32 border-4 border-white rounded-lg shadow-lg"
                />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl font-semibold text-purple-900 mb-2">Your Quick Sign-In QR Code</h3>
                <p className="text-sm text-purple-700 mb-4">
                  Share this QR code to let visitors quickly sign in with just their name, email, phone, and notes.
                </p>
                <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                  <Button
                    onClick={downloadQRCode}
                    className="bg-purple-600 text-white hover:bg-purple-700"
                    size="sm"
                  >
                    <Download size={16} className="mr-2" />
                    Download QR
                  </Button>
                  <Button
                    onClick={() => window.open(qrData.quick_signin_url, '_blank')}
                    variant="outline"
                    className="border-purple-300 text-purple-700 hover:bg-purple-100"
                    size="sm"
                  >
                    <ExternalLink size={16} className="mr-2" />
                    Preview Form
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Leads List */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-stone-900 mb-4">
            Recent Sign-Ins ({leads.length})
          </h2>

          {leads.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="text-stone-400" size={32} />
              </div>
              <p className="text-stone-600 mb-2">No Quick Sign-Ins yet</p>
              <p className="text-sm text-stone-500">
                Share your QR code to start capturing leads
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {leads.map((lead) => (
                <Card key={lead.id} className="p-4 hover:shadow-md transition-shadow border-l-4 border-purple-500">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-2">
                      {/* Name & Time */}
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-stone-900 flex items-center gap-2">
                          <User size={18} className="text-purple-600" />
                          {lead.name || 'N/A'}
                        </h3>
                        <span className="text-xs text-stone-500 flex items-center gap-1">
                          <Calendar size={14} />
                          {formatDate(lead.created_at)}
                        </span>
                      </div>

                      {/* Contact Info */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center gap-2 text-stone-700">
                          <Mail size={16} className="text-purple-500" />
                          <span>{lead.email || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-stone-700">
                          <Phone size={16} className="text-purple-500" />
                          <span>{formatPhone(lead.phone)}</span>
                        </div>
                      </div>

                      {/* Description/Notes */}
                      {lead.description && (
                        <div className="flex items-start gap-2 mt-2 p-3 bg-stone-50 rounded-lg">
                          <FileText size={16} className="text-purple-500 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-stone-700">{lead.description}</p>
                        </div>
                      )}

                      {/* Badge - Not a Lead */}
                      <div className="flex items-center gap-2 mt-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          Quick Sign-In
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default QuickSignInDashboard;
